import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import api from '../../services/api';
import { Match } from '../../types';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import './MatchHistory.css';

// Types
interface DatePickerProps {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  max?: string;
  min?: string;
}

interface StatusChipProps {
  status: string;
  size?: 'small' | 'medium';
}

interface MatchStats {
  totalMatches: number;
  homeWins: number;
  awayWins: number;
  draws: number;
  totalGoals: number;
  avgGoalsPerMatch: number;
}

// Custom DatePicker component
const DatePicker: React.FC<DatePickerProps> = memo(({ label, value, onChange, placeholder, max, min }) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value ? new Date(e.target.value) : null;
    onChange(date);
  }, [onChange]);

  return (
    <div className="date-picker-wrapper">
      <label className="date-picker-label">{label}</label>
      <input
        type="date"
        className="date-picker-input"
        value={value ? format(value, 'yyyy-MM-dd') : ''}
        onChange={handleChange}
        placeholder={placeholder}
        max={max}
        min={min}
      />
    </div>
  );
});

DatePicker.displayName = 'DatePicker';

// Status chip component with tooltip
const StatusChip: React.FC<StatusChipProps> = memo(({ status, size = 'medium' }) => {
  const getStatusConfig = (status: string) => {
    const configs: { [key: string]: { color: string; icon: string; tooltip: string } } = {
      finished: { 
        color: '#4caf50', 
        icon: '✅', 
        tooltip: 'Match completed with final score' 
      },
      scheduled: { 
        color: '#2196f3', 
        icon: '📅', 
        tooltip: 'Match scheduled for future date' 
      },
      live: { 
        color: '#f44336', 
        icon: '🔴', 
        tooltip: 'Match currently in progress' 
      },
      postponed: { 
        color: '#ff9800', 
        icon: '⏸️', 
        tooltip: 'Match postponed to later date' 
      },
      cancelled: { 
        color: '#9e9e9e', 
        icon: '❌', 
        tooltip: 'Match cancelled' 
      },
    };
    return configs[status] || configs.scheduled;
  };

  const config = getStatusConfig(status);

  return (
    <span 
      className={`status-chip ${size}`}
      style={{ backgroundColor: config.color }}
      title={config.tooltip}
    >
      <span className="status-icon">{config.icon}</span>
      <span className="status-text">{status.toUpperCase()}</span>
    </span>
  );
});

StatusChip.displayName = 'StatusChip';

// Stats Summary Component
const StatsSummary: React.FC<{ matches: Match[] }> = memo(({ matches }) => {
  const stats = useMemo<MatchStats>(() => {
    const finishedMatches = matches.filter(m => m.status === 'finished' && m.home_score !== null && m.away_score !== null);
    
    const homeWins = finishedMatches.filter(m => (m.home_score || 0) > (m.away_score || 0)).length;
    const awayWins = finishedMatches.filter(m => (m.home_score || 0) < (m.away_score || 0)).length;
    const draws = finishedMatches.filter(m => (m.home_score || 0) === (m.away_score || 0)).length;
    const totalGoals = finishedMatches.reduce((sum, m) => sum + (m.home_score || 0) + (m.away_score || 0), 0);
    
    return {
      totalMatches: matches.length,
      homeWins,
      awayWins,
      draws,
      totalGoals,
      avgGoalsPerMatch: finishedMatches.length > 0 ? totalGoals / finishedMatches.length : 0
    };
  }, [matches]);

  if (matches.length === 0) return null;

  return (
    <div className="stats-summary">
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">Total Matches</span>
          <span className="stat-value">{stats.totalMatches}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">🏠 Home Wins</span>
          <span className="stat-value">{stats.homeWins}</span>
          <span className="stat-percentage">
            ({stats.totalMatches > 0 ? ((stats.homeWins / stats.totalMatches) * 100).toFixed(1) : 0}%)
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">🤝 Draws</span>
          <span className="stat-value">{stats.draws}</span>
          <span className="stat-percentage">
            ({stats.totalMatches > 0 ? ((stats.draws / stats.totalMatches) * 100).toFixed(1) : 0}%)
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">🚌 Away Wins</span>
          <span className="stat-value">{stats.awayWins}</span>
          <span className="stat-percentage">
            ({stats.totalMatches > 0 ? ((stats.awayWins / stats.totalMatches) * 100).toFixed(1) : 0}%)
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">⚽ Total Goals</span>
          <span className="stat-value">{stats.totalGoals}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">📊 Avg Goals/Match</span>
          <span className="stat-value">{stats.avgGoalsPerMatch.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
});

StatsSummary.displayName = 'StatsSummary';

// Main Component
const MatchHistory: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [dataSource, setDataSource] = useState<'api' | 'database' | 'sample'>('api');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Filters
  const [league, setLeague] = useState<string>('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [team, setTeam] = useState<string>('');
  const [status, setStatus] = useState<string>('');

  const leagues = [
    { value: 'PL', label: '🏴󠁧󠁢󠁥󠁮󠁧󠁿 Premier League' },
    { value: 'PD', label: '🇪🇸 La Liga' },
    { value: 'SA', label: '🇮🇹 Serie A' },
    { value: 'BL1', label: '🇩🇪 Bundesliga' },
    { value: 'FL1', label: '🇫🇷 Ligue 1' },
    { value: 'DED', label: '🇳🇱 Eredivisie' },
    { value: 'PPL', label: '🇵🇹 Primeira Liga' },
  ];

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        page_size: rowsPerPage.toString()
      });
      
      if (league) params.append('league', league);
      if (fromDate) params.append('from_date', format(fromDate, 'yyyy-MM-dd'));
      if (toDate) params.append('to_date', format(toDate, 'yyyy-MM-dd'));
      if (team) params.append('team', team);
      if (status) params.append('status', status);
      
      const startTime = performance.now();
      const response = await api.get(`/matches/?${params.toString()}`);
      const endTime = performance.now();
      
      console.log(`📊 Match data fetched in ${(endTime - startTime).toFixed(0)}ms`);
      
      // Handle different response formats
      let matchesData: Match[] = [];
      let total = 0;
      
      if (response.data.results) {
        matchesData = response.data.results;
        total = response.data.count;
        setDataSource('api');
      } else if (Array.isArray(response.data)) {
        matchesData = response.data;
        total = response.data.length;
        setDataSource('database');
      } else {
        matchesData = [];
        total = 0;
      }
      
      // Validate data - check if we have real scores
      const hasRealScores = matchesData.some(m => 
        m.status === 'finished' && 
        m.home_score !== null && 
        m.away_score !== null
      );
      
      if (!hasRealScores && matchesData.length > 0) {
        console.warn('⚠️ No real scores found in data');
        setDataSource('sample');
      }
      
      setMatches(matchesData);
      setTotalCount(total);
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      setMatches([]);
      setTotalCount(0);
      setDataSource('sample');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, league, fromDate, toDate, team, status]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const handleApplyFilters = useCallback(() => {
    setPage(0);
    fetchMatches();
  }, [fetchMatches]);

  const handleClearFilters = useCallback(() => {
    setLeague('');
    setFromDate(null);
    setToDate(null);
    setTeam('');
    setStatus('');
    setPage(0);
  }, []);

  const handleChangePage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const getInitials = useCallback((name: string = ''): string => {
    return name.charAt(0).toUpperCase();
  }, []);

  const getDataSourceIcon = useCallback(() => {
    switch (dataSource) {
      case 'api': return '🌐';
      case 'database': return '💾';
      case 'sample': return '⚠️';
      default: return '❓';
    }
  }, [dataSource]);

  const getDataSourceText = useCallback(() => {
    switch (dataSource) {
      case 'api': return 'Live API Data';
      case 'database': return 'Cached Database';
      case 'sample': return 'Sample Data (No Real Matches)';
      default: return 'Unknown Source';
    }
  }, [dataSource]);

  return (
    <div className="match-history-container">
      <div className="match-history-paper">
        <div className="header-section">
          <h1 className="match-history-title">
            📊 Match History
          </h1>
          
          {/* Data Source Indicator */}
          <div className={`data-source-indicator ${dataSource}`} title={`Data source: ${getDataSourceText()}`}>
            <span className="source-icon">{getDataSourceIcon()}</span>
            <span className="source-text">{getDataSourceText()}</span>
            {lastUpdated && (
              <span className="last-updated">
                Updated: {format(lastUpdated, 'HH:mm:ss')}
              </span>
            )}
          </div>
        </div>

        {/* Stats Summary */}
        {!loading && matches.length > 0 && <StatsSummary matches={matches} />}

        {/* Filters */}
        <div className="filters-paper">
          <div className="filters-header">
            <h3 className="filters-title">🔍 Filter Matches</h3>
            {(league || fromDate || toDate || team || status) && (
              <span className="active-filters-badge">
                Active Filters
              </span>
            )}
          </div>
          
          <div className="filters-grid">
            <div className="filter-item">
              <label className="filter-label">League</label>
              <select 
                className="filter-select"
                value={league}
                onChange={(e) => setLeague(e.target.value)}
              >
                <option value="">All Leagues</option>
                {leagues.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-item">
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                placeholder="Start date"
                max={toDate ? format(toDate, 'yyyy-MM-dd') : undefined}
              />
            </div>
            
            <div className="filter-item">
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={setToDate}
                placeholder="End date"
                min={fromDate ? format(fromDate, 'yyyy-MM-dd') : undefined}
              />
            </div>
            
            <div className="filter-item">
              <label className="filter-label">Team</label>
              <input
                type="text"
                className="filter-input"
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                placeholder="Search by team name..."
              />
            </div>
            
            <div className="filter-item">
              <label className="filter-label">Status</label>
              <select 
                className="filter-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="finished">✅ Finished</option>
                <option value="scheduled">📅 Scheduled</option>
                <option value="live">🔴 Live</option>
                <option value="postponed">⏸️ Postponed</option>
                <option value="cancelled">❌ Cancelled</option>
              </select>
            </div>
          </div>
          
          <div className="filter-actions">
            <button 
              className="btn btn-outline"
              onClick={handleClearFilters}
              disabled={loading}
            >
              Clear Filters
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleApplyFilters}
              disabled={loading}
            >
              {loading ? <div className="btn-spinner"></div> : 'Apply Filters'}
            </button>
          </div>
        </div>

        {/* Results Count */}
        {!loading && matches.length > 0 && (
          <div className="results-count">
            Showing {matches.length} of {totalCount} matches
          </div>
        )}

        {/* Matches Table */}
        <div className="table-container">
          <table className="matches-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>League</th>
                <th>Home Team</th>
                <th className="text-center">Score</th>
                <th>Away Team</th>
                <th className="text-center">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center loading-cell">
                    <div className="spinner"></div>
                    <p className="loading-text">Loading real match data...</p>
                  </td>
                </tr>
              ) : matches.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center empty-cell">
                    <div className="empty-icon">📭</div>
                    <h3 className="empty-title">No matches found</h3>
                    <p className="empty-text">
                      {dataSource === 'sample' 
                        ? 'No real match data available. Please check back later.'
                        : 'Try adjusting your filters to see more matches'}
                    </p>
                    {dataSource === 'sample' && (
                      <button 
                        className="btn btn-primary refresh-btn"
                        onClick={fetchMatches}
                      >
                        🔄 Refresh Data
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                matches.map((match) => (
                  <tr key={match.id} className="match-row">
                    <td>
                      <div className="date-cell">
                        <span className="date">
                          {format(parseISO(match.match_date), 'dd MMM yyyy')}
                        </span>
                        <span className="time">
                          {format(parseISO(match.match_date), 'HH:mm')}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="league-chip" title={match.league}>
                        {match.league}
                      </span>
                    </td>
                    <td>
                      <div className="team-cell">
                        {match.home_team_logo ? (
                          <img 
                            src={match.home_team_logo} 
                            alt={match.home_team_name} 
                            className="team-logo"
                            loading="lazy"
                          />
                        ) : (
                          <div className="team-avatar home">
                            {getInitials(match.home_team_name)}
                          </div>
                        )}
                        <span className="team-name">{match.home_team_name}</span>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className={`score ${match.status === 'live' ? 'live-score' : ''}`}>
                        {match.home_score ?? '-'} : {match.away_score ?? '-'}
                      </span>
                      {match.home_xg && match.away_xg && match.status === 'finished' && (
                        <span className="xg-info" title="Expected Goals (xG)">
                          xG: {match.home_xg.toFixed(2)} - {match.away_xg.toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="team-cell reverse">
                        <span className="team-name">{match.away_team_name}</span>
                        {match.away_team_logo ? (
                          <img 
                            src={match.away_team_logo} 
                            alt={match.away_team_name} 
                            className="team-logo"
                            loading="lazy"
                          />
                        ) : (
                          <div className="team-avatar away">
                            {getInitials(match.away_team_name)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="text-center">
                      <StatusChip status={match.status} />
                    </td>
                    <td className="text-right">
                      <button
                        className="btn-view"
                        onClick={() => window.location.href = `/matches/${match.id}`}
                        title="View match details"
                      >
                        View Details →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && matches.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-controls">
              <div className="rows-per-page">
                <label>Rows per page:</label>
                <select 
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  className="rows-select"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              
              <div className="pagination-info">
                {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount}
              </div>
              
              <div className="pagination-buttons">
                <button
                  className="pagination-btn"
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 0}
                  title="Previous page"
                >
                  ‹
                </button>
                <button
                  className="pagination-btn"
                  onClick={() => handleChangePage(page + 1)}
                  disabled={(page + 1) * rowsPerPage >= totalCount}
                  title="Next page"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data Attribution */}
        <div className="data-attribution">
          <p>
            Data provided by football-data.org • 
            <span className={`data-quality ${dataSource}`}>
              {getDataSourceIcon()} {getDataSourceText()}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default memo(MatchHistory);